import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Minus, Plus } from "lucide-react";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import { Input } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/input";
import { Label } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/label";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/select";
import { useToast } from "@/projects/Admin_Bite_Balance/bite-balance/src/hooks/use-toast";

const servingDescriptions = [
  "plate",
  "bowl",
  "cup",
  "glass",
  "piece",
  "grams",
  "other",
];
const servingSizes = ["small", "medium", "large"];

const EditFoodItem = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const item = location.state?.item;

  const [foodName, setFoodName] = useState(item?.name || "");
  const [servingDescription, setServingDescription] = useState(
    item?.servingDescription || "plate"
  );
  const [servingSize, setServingSize] = useState(
    item?.servingSizeLabel || "medium"
  );
  const [servingCount, setServingCount] = useState(item?.servingCount || 1);
  const [customServing, setCustomServing] = useState(item?.customServing || "");

  const handleSaveChanges = () => {
    if (!foodName.trim()) {
      toast({
        title: "Error",
        description: "Please enter a food name",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Success",
      description: "Food item updated successfully",
    });
    navigate("/meals/prediction");
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <div className="sticky top-0 z-20 border-b border-border bg-background">
        <div className="flex items-center justify-between p-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/meals/prediction")}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-bold">Edit Food Item</h1>
          <div className="w-10" />
        </div>
      </div>

      <div className="space-y-6 p-4">
        <Card className="space-y-6 border-2 p-6">
          <div className="space-y-2">
            <Label htmlFor="foodName" className="font-bold">
              Food Name
            </Label>
            <Input
              id="foodName"
              placeholder="e.g., Grilled Chicken Breast"
              value={foodName}
              onChange={e => setFoodName(e.target.value)}
              className="border-2"
            />
          </div>

          {/* Serving Description */}
          <div className="space-y-2">
            <Label className="font-bold">Serving Description</Label>
            <Select
              value={servingDescription}
              onValueChange={setServingDescription}
            >
              <SelectTrigger className="border-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {servingDescriptions.map(desc => (
                  <SelectItem key={desc} value={desc} className="capitalize">
                    {desc}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(servingDescription === "other" ||
              servingDescription === "grams") && (
              <Input
                placeholder={
                  servingDescription === "grams"
                    ? "e.g., 150"
                    : "e.g., 250g or 200mL"
                }
                value={customServing}
                onChange={e => setCustomServing(e.target.value)}
                className="mt-2 border-2"
                type={servingDescription === "grams" ? "number" : "text"}
              />
            )}
          </div>

          {/* Serving Size */}
          <div className="space-y-2">
            <Label className="font-bold">Serving Size</Label>
            <div className="flex gap-2">
              {servingSizes.map(size => (
                <Button
                  key={size}
                  type="button"
                  variant={servingSize === size ? "default" : "outline"}
                  className={`flex-1 capitalize ${servingSize !== size ? "border-2" : ""}`}
                  onClick={() => setServingSize(size)}
                >
                  {size}
                </Button>
              ))}
            </div>
          </div>

          {/* Serving Count */}
          <div className="space-y-2">
            <Label className="font-bold">Number of Servings</Label>
            <div className="flex items-center justify-center gap-4">
              <Button
                variant="outline"
                size="icon"
                className="border-2"
                onClick={() => setServingCount(Math.max(1, servingCount - 1))}
              >
                <Minus className="h-4 w-4" />
              </Button>
              <span className="w-8 text-center text-xl font-bold">
                {servingCount}
              </span>
              <Button
                variant="outline"
                size="icon"
                className="border-2"
                onClick={() => setServingCount(servingCount + 1)}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="space-y-3 pt-4">
            <Button className="w-full" size="lg" onClick={handleSaveChanges}>
              Save Changes
            </Button>
            <Button
              variant="outline"
              className="w-full border-2"
              size="lg"
              onClick={() => navigate("/meals/prediction")}
            >
              Cancel
            </Button>
          </div>
        </Card>

        {item && (
          <Card className="border-2 bg-muted p-4">
            <h3 className="mb-3 font-bold">Current Nutrition Info</h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Calories:</span>
                <span className="font-semibold">{item.calories} kcal</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Protein:</span>
                <span className="font-semibold">{item.protein}g</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Carbs:</span>
                <span className="font-semibold">{item.carbs}g</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Fat:</span>
                <span className="font-semibold">{item.fat}g</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Sugar:</span>
                <span className="font-semibold">{item.sugar}g</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Fiber:</span>
                <span className="font-semibold">{item.fiber}g</span>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default EditFoodItem;
